import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, LogOut, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/utils/cn'
import {
  HEADER_BAND_HEIGHT_PX,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '@/constants/layout'
import {
  getCollapsedSidebarLogo,
  getExpandedSidebarLogo,
  getExpandedSidebarLogoFallback,
} from './sidebarLogos'
import { NAV_SECTION_THEMES } from './navIconStyles'
import { canSeeNavItem } from '@/config/routeAccess'
import { SidebarItem } from './sidebarNavItem'
import { CONECTA_NAV_SECTIONS } from './sidebarNavSections'
import { isRocketInstance, ROCKET_ENABLED_MODULES } from '@/config/instanceProfile'
import { canAccessEmpresasContatos } from '@/utils/userEventMode'
import { canAccessGestoresModule } from '@/config/gestorAccess'
import type { Role } from '@/types'


const SidebarNav = ({
  collapsed,
  onNavigate,
  isDark,
}: {
  collapsed: boolean
  onNavigate?: () => void
  isDark: boolean
}) => {
  const { user } = useAuthStore()
  const role = (user?.role ?? 'bdr') as Role
  const isAdmin = role === 'admin'
  const canSeeAdminSection = isAdmin || (isRocketInstance && role === 'manager')
  const sections = CONECTA_NAV_SECTIONS.filter((s) => !s.adminOnly || canSeeAdminSection)
    .filter((s) => {
      if (!isRocketInstance) return true
      return (ROCKET_ENABLED_MODULES as readonly string[]).includes(s.moduleId)
    })
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!canSeeNavItem(role, item, user)) return false
        if (
          (item.to === '/empresas' || item.to.startsWith('/empresas?')) &&
          !canAccessEmpresasContatos(user)
        ) {
          return false
        }
        if (item.to === '/gestores' && !canAccessGestoresModule(user)) return false
        return true
      }),
    }))
    .filter((section) => section.items.length > 0)

  return (
    <nav className='relative z-10 flex-1 overflow-y-auto px-2 pb-4 pt-2 custom-scrollbar'>
      {sections.map((section, index) => (
        <div key={section.label} className='mb-1'>
          {!collapsed && (
            <p
              className={cn(
                'mb-2 px-3 pt-5 text-[11px] font-semibold uppercase tracking-wide first:pt-0',
                section.themeKey !== 'financeiro' && 'text-text-muted',
              )}
              style={
                section.themeKey === 'financeiro'
                  ? { color: NAV_SECTION_THEMES.financeiro.color }
                  : undefined
              }
            >
              {section.label}
            </p>
          )}
          {collapsed && index > 0 && (
            <div
              className={cn('my-2 mx-2 border-t', isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]')}
            />
          )}
          <ul className='space-y-0.5'>
            {section.items.map((item) => (
              <li key={item.to}>
                <SidebarItem
                  {...item}
                  sectionKey={section.themeKey}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  isDark={isDark}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  )
}

function SidebarShell({
  collapsed,
  onNavigate,
  onClose,
  showClose,
}: {
  collapsed: boolean
  onNavigate?: () => void
  onClose?: () => void
  showClose?: boolean
}) {
  const { theme } = useThemeStore()
  const { logout } = useAuthStore()
  const { isDark } = useTheme()
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore()
  const navigate = useNavigate()

  const isCollapsed = collapsed ?? sidebarCollapsed
  const collapsedLogo = getCollapsedSidebarLogo(theme.mode)
  const expandedLogo = getExpandedSidebarLogo(theme.mode, theme)

  const handleLogout = async () => {
    onNavigate?.()
    await logout()
    navigate('/login', { replace: true })
  }

  const shellBorder = isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]'

  return (
    <>
      <div
        className={cn(
          'relative z-10 flex shrink-0 border-b',
          shellBorder,
          isCollapsed
            ? 'flex-col items-center justify-center gap-1 px-1'
            : 'flex-row items-center justify-between gap-2 px-4',
        )}
        style={{ height: HEADER_BAND_HEIGHT_PX }}
      >
        <img
          src={isCollapsed ? collapsedLogo : expandedLogo}
          alt={theme.companyName}
          onError={(e) => {
            const img = e.target as HTMLImageElement
            img.src = isCollapsed
              ? getCollapsedSidebarLogo(theme.mode)
              : getExpandedSidebarLogoFallback(theme.mode)
          }}
          className={cn(
            'object-contain',
            isCollapsed ? 'h-7 w-7' : 'h-10 w-auto max-w-[180px]',
          )}
        />
        {showClose ? (
          <button
            type='button'
            onClick={onClose}
            className={cn(
              'shrink-0 rounded-md p-1 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center',
              isDark
                ? 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#334155]',
            )}
            aria-label='Fechar menu'
          >
            <X size={18} />
          </button>
        ) : (
          <button
            type='button'
            onClick={toggleSidebarCollapsed}
            className={cn(
              'shrink-0 rounded-md p-1 transition-colors',
              isDark
                ? 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#334155]',
            )}
            aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      <SidebarNav collapsed={isCollapsed} onNavigate={onNavigate} isDark={isDark} />

      <div
        className={cn(
          'relative z-10 border-t py-3',
          shellBorder,
          isCollapsed ? 'px-1' : 'px-3',
        )}
      >
        <button
          type='button'
          onClick={handleLogout}
          title='Sair'
          className={cn(
            'flex w-full items-center rounded-xl py-2 text-[13px] transition-colors',
            isCollapsed ? 'justify-center px-2' : 'gap-2.5 px-3',
            isDark
              ? 'text-text-muted hover:bg-danger/[0.06] hover:text-danger'
              : 'text-[#64748b] hover:bg-red-50 hover:text-red-600',
          )}
        >
          <LogOut size={16} />
          {!isCollapsed && 'Sair'}
        </button>
      </div>
    </>
  )
}

const MobileDrawer = () => {
  const { isMobileOpen, closeMobileMenu } = useUIStore()

  return (
    <AnimatePresence>
      {isMobileOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeMobileMenu}
            className='fixed inset-0 bg-black/60 z-40 md:hidden'
          />

          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className='fixed top-0 left-0 bottom-0 w-[17rem] bg-bg-sidebar border-r border-[#eef2f7] dark:border-white/[0.06] flex flex-col z-50 md:hidden'
          >
            <SidebarShell
              collapsed={false}
              onNavigate={closeMobileMenu}
              onClose={closeMobileMenu}
              showClose
            />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

const DesktopSidebar = () => {
  const { sidebarCollapsed } = useUIStore()
  const { isDark } = useTheme()
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED

  return (
    <aside
      style={{ width: sidebarWidth }}
      className={cn(
        'hidden md:flex h-screen shrink-0 flex-col border-r bg-bg-sidebar transition-[width] duration-200 ease-out select-none',
        isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]',
      )}
    >
      <SidebarShell collapsed={sidebarCollapsed} />
    </aside>
  )
}

export function Sidebar() {
  return (
    <>
      <DesktopSidebar />
      <MobileDrawer />
    </>
  )
}
